import { Component, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { flatten } from '../../../utils/flatten';
import { IUserManagementFilter } from '../../../interfaces/user-management-filter';
import { UserService } from '../../../services';
import { IPageResult } from '../../../interfaces/page-result';
import { IUserReport } from '../../../interfaces/user-report.interface';
import { LazyLoadEvent, SelectItem } from 'primeng';
import { OrganisationsService } from '../../../services';
import { _deepClone } from 'fast-json-patch/module/helpers';

@Component({
  selector: 'admin-user-management',
  styleUrls: ['./user-management.component.scss'],
  templateUrl: './user-management.component.html',
})
export class AdminUserManagementComponent implements OnInit {
  constructor(private userService: UserService, private orgService: OrganisationsService) {}

  public page: IPageResult<IUserReport> = {
    items: [],
    total: 0,
  };

  public filterVal: IUserManagementFilter;
  public filter: Subject<IUserManagementFilter>;

  isLoaded = false;

  public customers: SelectItem[];
  private departmentsMap: Map<number, SelectItem[]> = new Map<number, SelectItem[]>();
  public departments: SelectItem[] = [];
  public activeState: any;
  csvDownloading = false;
  csvFilterVal: IUserManagementFilter; // mirrors filter from last search

  ngOnInit() {
    this.orgService.getAllCustomers().then((data) => {
      this.customers = [];
      data.forEach((o) => {
        this.customers.push({ label: o.name, value: o.id });

        if (!this.departmentsMap.has(o.id)) {
          this.departmentsMap.set(
            o.id,
            o.departments.map((d) => {
              return { label: `${d.name} (${o.name})`, value: d.id };
            }),
          );
        }
      });
    });

    this.initDefaultFilter();
    this.filter = new Subject<IUserManagementFilter>();

    // subscribe to filter changes with a debounce time
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    // set default filter
    this.filter.next(this.filterVal);
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'OrganisationName',
      sortOrder: 1,
      filters: {
        userName: {},
        organisationIds: {},
        departmentIds: {},
        active: {},
      },
    };
  }

  load(filter: IUserManagementFilter) {
    this.isLoaded = false;

    this.userService.getUserManagementReport(flatten(filter)).then((result) => {
      this.page.items = result.items;
      this.page.total = result.total;
      this.isLoaded = true;
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.filter.next(this.filterVal);
  }

  search() {
    this.setCSVFilterVal();
    this.load(this.filterVal);
  }

  customerSelect() {
    this.departments = [];

    // filter available departments by selected customer(s)
    if (
      this.filterVal.filters.organisationIds &&
      this.filterVal.filters.organisationIds.value.length > 0
    ) {
      this.filterVal.filters.organisationIds.value.forEach((id) => {
        this.departments.push(...this.departmentsMap.get(id));
      });
    } else {
      this.filterVal.filters.departmentIds = { value: null };
    }
  }

  clearFilter() {
    this.filterVal.filters.departmentIds = { value: null };
    this.filterVal.filters.organisationIds = { value: null };
    this.activeState = null;

    this.search();
  }

  activeStateChanged() {
    this.filterVal.filters.active.value = this.activeState;
  }

  setCSVFilterVal() {
    this.csvFilterVal = _deepClone(this.filterVal);
  }

  exportCSV() {
    if (!this.csvFilterVal) {
      this.setCSVFilterVal();
    }

    this.csvDownloading = true;
    this.userService.downloadUserManagementReport(this.csvFilterVal).then(() => {
      this.csvDownloading = false;
    });
  }
}
